import { BrandLogo } from '../components/Brand';
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { flashcardsAPI, questionsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { 
    Scale, 
    BookOpen, 
    ArrowLeft,
    RotateCcw,
    ChevronLeft,
    ChevronRight,
    Eye,
    EyeOff,
    Shuffle
} from "lucide-react";
import { toast } from "sonner";

export default function StudyModePage() {
    const [flashcards, setFlashcards] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState("all");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
        loadSubjects();
    }, []);
    
    const loadSubjects = async () => {
        try {
            const response = await questionsAPI.getSubjects();
            setSubjects(response.data.subjects);
        } catch (error) {
            console.error("Failed to load subjects:", error);
        }
    };
    
    const loadFlashcards = async () => {
        setLoading(true);
        try {
            const subjectParam = selectedSubject === "all" ? undefined : selectedSubject;
            const response = await flashcardsAPI.getFlashcards({
                subject: subjectParam,
                count: 20
            });
            if (response.data.length === 0) {
                toast.info("No flashcards found. Try seeding questions from the dashboard!");
            }
            setFlashcards(response.data);
            setCurrentIndex(0);
            setShowAnswer(false);
        } catch (error) {
            toast.error("Failed to load flashcards");
        } finally {
            setLoading(false);
        }
    };
    
    const shuffleCards = () => {
        const shuffled = [...flashcards].sort(() => Math.random() - 0.5);
        setFlashcards(shuffled);
        setCurrentIndex(0);
        setShowAnswer(false);
    };
    
    const nextCard = () => {
        if (currentIndex < flashcards.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setShowAnswer(false);
        }
    };
    
    const prevCard = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setShowAnswer(false);
        }
    };
    
    const currentCard = flashcards[currentIndex];
    
    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 glass border-b border-slate-800 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Back to Dashboard</span>
                    </Link>
                    
                    <BrandLogo />
                    
                    <div className="w-20" />
                </div>
            </nav>
            
            <main className="max-w-4xl mx-auto px-6 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
                        <BookOpen className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
                        Study Mode
                    </h1>
                    <p className="text-slate-400">Flashcards for focused learning</p>
                </motion.div>
                
                {/* Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-col sm:flex-row gap-4 items-center justify-center mb-8"
                >
                    <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                        <SelectTrigger className="w-full sm:w-64 bg-slate-950/50 border-slate-800" data-testid="study-subject-select">
                            <SelectValue placeholder="All Subjects" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Subjects</SelectItem>
                            {subjects.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    <Button
                        onClick={loadFlashcards}
                        disabled={loading}
                        className="rounded-full bg-emerald-500 hover:bg-emerald-600"
                        data-testid="load-flashcards-btn"
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <>
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Load Flashcards
                            </>
                        )}
                    </Button>
                </motion.div>
                
                {/* Flashcard Display */}
                {flashcards.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        {/* Progress */}
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">
                                Card {currentIndex + 1} of {flashcards.length}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={shuffleCards}
                                className="text-slate-400 hover:text-white"
                                data-testid="shuffle-btn"
                            >
                                <Shuffle className="w-4 h-4 mr-2" />
                                Shuffle
                            </Button>
                        </div>
                        
                        {/* Flashcard */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Card 
                                    className="glass border-slate-800 rounded-2xl cursor-pointer min-h-[300px] flex flex-col"
                                    onClick={() => setShowAnswer(!showAnswer)}
                                    data-testid="flashcard"
                                >
                                    <CardContent className="p-6 flex-1 flex flex-col">
                                        <Badge className="self-start mb-4 bg-slate-800 text-slate-400 border-0">
                                            {currentCard.subject}
                                        </Badge>
                                        
                                        <div className="flex-1 flex flex-col justify-center">
                                            {/* Question */}
                                            <p className="text-white text-lg leading-relaxed mb-6" data-testid="flashcard-question">
                                                {currentCard.text}
                                            </p>
                                            
                                            {/* Options */}
                                            {!showAnswer && (
                                                <div className="space-y-2">
                                                    {currentCard.options.map((option) => (
                                                        <div
                                                            key={option.id}
                                                            className="flex items-start gap-2 text-slate-400 text-sm"
                                                        >
                                                            <span className="font-mono font-bold">{option.id}.</span>
                                                            <span>{option.text}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Answer */}
                                            <AnimatePresence>
                                                {showAnswer && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: "auto" }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        className="mt-4 pt-4 border-t border-slate-800"
                                                    >
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <Badge className="bg-emerald-500/20 text-emerald-400 border-0">
                                                                Correct Answer: {currentCard.correct_option_id}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-slate-300 text-sm leading-relaxed" data-testid="flashcard-answer">
                                                            {currentCard.explanation}
                                                        </p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                        
                                        {/* Tap hint */}
                                        <div className="flex items-center justify-center gap-2 mt-4 text-slate-500 text-sm">
                                            {showAnswer ? (
                                                <EyeOff className="w-4 h-4" />
                                            ) : (
                                                <Eye className="w-4 h-4" />
                                            )}
                                            <span>Tap to {showAnswer ? "hide" : "reveal"} answer</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </AnimatePresence>
                        
                        {/* Navigation */}
                        <div className="flex items-center justify-between mt-6">
                            <Button
                                variant="outline"
                                onClick={prevCard}
                                disabled={currentIndex === 0}
                                className="rounded-full border-slate-700"
                                data-testid="prev-card-btn"
                            >
                                <ChevronLeft className="w-5 h-5 mr-1" />
                                Previous
                            </Button>
                            
                            <div className="flex gap-1">
                                {flashcards.map((_, i) => (
                                    <div
                                        key={i}
                                        className={`w-2 h-2 rounded-full transition-colors ${
                                            i === currentIndex ? "bg-primary" : "bg-slate-700"
                                        }`}
                                    />
                                ))}
                            </div>
                            
                            <Button
                                variant="outline"
                                onClick={nextCard}
                                disabled={currentIndex === flashcards.length - 1}
                                className="rounded-full border-slate-700"
                                data-testid="next-card-btn"
                            >
                                Next
                                <ChevronRight className="w-5 h-5 ml-1" />
                            </Button>
                        </div>
                    </motion.div>
                ) : (
                    <Card className="glass border-slate-800 rounded-2xl">
                        <CardContent className="p-8 text-center">
                            <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400 mb-2">No flashcards loaded yet</p>
                            <p className="text-slate-500 text-sm">
                                Select a subject and click "Load Flashcards" to start studying
                            </p>
                        </CardContent>
                    </Card>
                )}
            </main>
        </div>
    );
}
